import { Box, CardContent, Grid, Stack } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcCard, MprcChipChoice, MprcContainer, MprcFormSubHeader, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { ManagedEnrollmentClassroomListResponse, ManageEnrollOperation, UserInfoDtoPaginatedList, } from "generated/CoreApi_v1";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from "providers/SessionProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { EditEnrollmentStepProps, EnrollmentOperation, EnrollmentOperationContextType } from "views/lms/enrollment/Home";
import { EnrollmentExcludeOptions, EnrollmentExtraTimeOptions, useEnrollmentManagementContext } from "views/lms/enrollment/Home/EnrollmentManagementWrapper";
import { EnrollOperationOptionCancel, EnrollOperationOptionEnrollForRecycling, EnrollOperationOptionEnrollment, EnrollOperationOptionExclude, EnrollOperationOptionExtraTime, EnrollOperationOptionFinalize, EnrollOperationOptionRevoke, EnrollOperationOptionUndoRevoke } from 'views/lms/enrollment/Home/Operation/OperationBoxOption';
import { OperationSearchUsers } from "views/lms/enrollment/Home/Operation/OperationSearchUsers";
import { OperationSelectedUsers } from "views/lms/enrollment/Home/Operation/OperationSelectedUsers";

////////////////////////////////////////////////////////////////////////////

const EnrollmentOperationContext = React.createContext<EnrollmentOperationContextType>(null!);

export const useEnrollmentOperationContext = () => React.useContext(EnrollmentOperationContext);

////////////////////////////////////////////////////////////////////////////

export const OperationBox: React.FC<EditEnrollmentStepProps> = (props) => {
    const { permissionExplorer } = useSession();
    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const location = useLocation();

    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    ////////////////////////////////////////////////////////////////////////////

    const getOperationItems = useCallback((): EnrollmentOperation[] => {
        const options = [] as EnrollmentOperation[];

        const hasMainPermission = permissionExplorer.hasOne([
            CorePermissions.TrainingManageEnrollments,
            CorePermissions.TrainingManageEnrollmentsTargetAudience,
            CorePermissions.TrainingCoordinate
        ])
            ||
            training.isCoordinator == true;

        const hasRevokePermission = permissionExplorer.hasOne([CorePermissions.TrainingRevokeExemptExcludeEnrollments]);

        if (hasMainPermission) {
            options.push({ value: ManageEnrollOperation.Enrollment, text: t("Enrollment.StepOperation.Operation.EnrollmentText"), disabled: training.classCount == 0 });
            options.push({ value: ManageEnrollOperation.Cancel, text: t("Enrollment.StepOperation.Operation.CancelText"), disabled: false });
            options.push({ value: ManageEnrollOperation.AllowExtraTime, text: t("Enrollment.StepOperation.Operation.AllowExtraTimeText"), disabled: training.classCount == 0 });
        }

        if (hasRevokePermission) {
            options.push({ value: ManageEnrollOperation.Revoke, text: t("Enrollment.StepOperation.Operation.RevokeText"), disabled: false });
            options.push({ value: ManageEnrollOperation.UndoRevoke, text: t("Enrollment.StepOperation.Operation.UndoRevokeText"), disabled: false });
        }

        if (hasMainPermission)
            options.push({ value: ManageEnrollOperation.ReEnrollForRecycling, text: t("Enrollment.StepOperation.Operation.ReEnrollForRecyclingText"), disabled: false });

        if (hasRevokePermission)
            options.push({ value: ManageEnrollOperation.Exclude, text: t("Enrollment.StepOperation.Operation.DeleteText"), disabled: false });

        options.push({ value: ManageEnrollOperation.Finalize, text: t("Enrollment.StepOperation.Operation.FinalizeEnrollText"), disabled: false });

        return options;
    }, [training]);

    ////////////////////////////////////////////////////////////////////////////

    const [loadingSelected, setLoadingSelected] = useState<boolean>(false);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();

    //Informações da lista de busca de usuários
    const [selectedUsers, setSelectedUsers] = useState<UserInfoDtoPaginatedList>();

    ///
    const changeOperation = (operation: EnrollmentOperation) => {
        ctx.setOperation(operation.value);
        ctx.setOptions((state) => { return { ...state, ImportedFile: false }; });
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (props.action == "confirm") {
            setPosting(true);
            api.v1.enrollmentOperation.saveEnroll({
                trainingId: ctx.training!.trainingId,
                classroomId: ctx.classroom?.classroomId ?? null,
                operation: ctx.operation!,
                enrollment: ctx.operation != ManageEnrollOperation.Enrollment
                    ? null
                    : { forcePrerequisites: ctx.options.enrollForcePrerequisites, sendNotification: ctx.options.enrollSendNotification },
                cancel: ctx.operation != ManageEnrollOperation.Cancel
                    ? null
                    : { reasonId: ctx.options.cancelReason[0].key, remarks: ctx.options.cancelRemarks },
                extraTime: ctx.operation != ManageEnrollOperation.AllowExtraTime
                    ? null
                    :
                    {
                        aditionalDays: ctx.options.extraTimeOption == EnrollmentExtraTimeOptions.Days ? ctx.options.aditionalDays : 0,
                        untilDate: ctx.options.extraTimeOption == EnrollmentExtraTimeOptions.Date ? ctx.options.untilDate : null
                    },
                revoke: ctx.operation != ManageEnrollOperation.Revoke
                    ? null
                    : { reason: ctx.options.revokeReason },
                undoRevoke: ctx.operation != ManageEnrollOperation.UndoRevoke
                    ? null
                    : { reason: ctx.options.undoRevokeReason },
                reEnroll: ctx.operation != ManageEnrollOperation.ReEnrollForRecycling
                    ? null
                    :
                    {
                        revokePrevVersion: ctx.options.reEnrollRevokeApprovalPrevVersion,
                        reuseComponent: ctx.options.reEnrollReuseComponent
                    },
                exclude: ctx.operation != ManageEnrollOperation.Exclude
                    ? null
                    :
                    {
                        latest: ctx.options.excludeOption == EnrollmentExcludeOptions.OnlyLatestEnrollments,
                        reason: ctx.options.excludeReason,
                        sendNotification: ctx.options.excludeSendNotification
                    }
            }).then(() => {
                props.goNextStep();
            }).catch(e => {
                MprcRequestSnackbarErrorReport(e);
            }).finally(() => setPosting(false));
        }
    }, [props.action]);

    useEffect(() => {
        if (ctx.operation == undefined) {
            ctx.setOperation(training.classCount == 0 && getOperationItems().some(x => x.value == ManageEnrollOperation.Revoke)
                ? ManageEnrollOperation.Revoke
                : ManageEnrollOperation.Enrollment
            );
        }
    }, [ctx.operation, ctx.training]);

    useEffect(() => {
        if (ctx.operation != undefined) {
            setLoadingUsers(true);
            setLoadingSelected(true);
        }
    }, [ctx.operation, ctx.classroom]);

    useEffect(() => {
        if (location.state && location.state.lastClassroomCreated)
            ctx.setClassroom(location.state.lastClassroomCreated as ManagedEnrollmentClassroomListResponse);
    }, []);

    ////////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={posting} type={"request"} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <EnrollmentOperationContext.Provider
                value={
                    {
                        loadingSelected,
                        setLoadingSelected,
                        loadingUsers,
                        setLoadingUsers,
                        posting,
                        setPosting,
                        selectedUsers,
                        setSelectedUsers,
                    }
                }
            >
                <MprcCard>
                    <MprcCardFilterBox>
                        <MprcFormSubHeader
                            margin="normal"
                            title={t("Enrollment.StepOperation.Title")}
                            subtitle={ctx.training?.name}
                        />
                        <Stack direction="row" justifyContent="flex-start" alignContent="flex-start" flexWrap="wrap">
                            {
                                getOperationItems().map(x =>
                                    <MprcChipChoice
                                        label={x.text.toUpperCase()}
                                        key={x.value}
                                        selected={x.value == ctx.operation}
                                        onClick={() => changeOperation(x)}
                                    />
                                )
                            }
                        </Stack>
                    </MprcCardFilterBox>
                    <CardContent>
                        <Stack direction="column" spacing={1}>
                            {
                                ctx.operation == ManageEnrollOperation.Enrollment
                                    ? <EnrollOperationOptionEnrollment />
                                    : ctx.operation == ManageEnrollOperation.Cancel
                                        ? <EnrollOperationOptionCancel />
                                        : ctx.operation == ManageEnrollOperation.AllowExtraTime
                                            ? <EnrollOperationOptionExtraTime />
                                            : ctx.operation == ManageEnrollOperation.Revoke
                                                ? <EnrollOperationOptionRevoke />
                                                : ctx.operation == ManageEnrollOperation.UndoRevoke
                                                    ? <EnrollOperationOptionUndoRevoke />
                                                    : ctx.operation == ManageEnrollOperation.ReEnrollForRecycling
                                                        ? <EnrollOperationOptionEnrollForRecycling />
                                                        : ctx.operation == ManageEnrollOperation.Exclude
                                                            ? <EnrollOperationOptionExclude />
                                                            : <EnrollOperationOptionFinalize />
                            }
                        </Stack>
                    </CardContent>
                </MprcCard>
                <Box marginTop={2}>
                    <MprcContainer>
                        <Grid container spacing={3} wrap="nowrap">
                            <Grid size={{ xs: 8 }}>
                                {
                                    !!ctx.training?.trainingId && !!ctx.operation &&
                                    <OperationSearchUsers {...props} />
                                }
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                {
                                    !!ctx.training?.trainingId && !!ctx.operation &&
                                    <OperationSelectedUsers {...props} />
                                }
                            </Grid>
                        </Grid>
                    </MprcContainer>
                </Box>
            </EnrollmentOperationContext.Provider>
        </>
    );
};
